import { getSensorTool } from "integrations/sensortool"
import store from "integrations/redux/store"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { decode } from "@msgpack/msgpack"
import { Position } from "@deck.gl/core"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"

export const getInfraredImageS3Key = (
    orgId: string,
    projectId: string,
    flightId: string,
    filename: string
) => `public/org/${orgId}/fire/${projectId}/flight/${flightId}/infrared/original/${filename}`

export const getSampleInfraredImageS3Key = (flightId: string, filename: string) =>
    `public/sample-data/flight/${flightId}/infrared/original/${filename}`

export const retrieveInfraredImageCornersFromFlightId = async (flightId: string) => {
    const workspaceId = selectActiveWorkspaceId()(store.getState())
    const projectId = selectActivatedFireId(store.getState())

    const key = `public/org/${workspaceId}/fire/${projectId}/flight/${flightId}/orthomosaic-output/odm_orthophoto/corners.msg`

    const bytes = await fileManagementApi.fetchFile(key).then((res) => res.bytes())

    const corners = decode(bytes) as Record<string, [Position, Position, Position, Position]>

    const newCorners = Object.entries(corners).reduce(
        (curr, [key, value]) => {
            value.unshift(value.pop()!)
            curr[key] = value
            return curr
        },
        {} as Record<string, [Position, Position, Position, Position]>
    )

    return newCorners
}

export const retrieveInfraredImageFromStorageKey = async (
    key: string,
    isDemo = false
    // this will be needed to provide perimeter/center point for drawing the hotspot onto canvas image
    // context: InfraredImageContext = {}
): Promise<
    | {
          imageUrl: string
          imagram: Float32Array
          imagramY: Float32Array
          loading: boolean
          width: number
          height: number
      }
    | undefined
> => {
    const res = await fileManagementApi.fetchFile(key, { isDemo })

    try {
        // @todo -- we have the capability now to use analyzeImageFromArrayBuffer from the same package
        // confirm in local tests that there are no side effects and switch that to potentially avoid
        // the file.type issues
        const processedImage = await (
            await getSensorTool()
        ).analyzeImageFromArrayBuffer(await res.arrayBuffer())
        const image = await processedImage.getVisibleImageAsBlob()

        const rgb = await image.rgb()
        const stats = await image.stats()
        const imagram = await image.imagram()
        const imagramY = await image.imagram_y()
        if (stats === undefined || imagram == undefined || imagramY === undefined) {
            // image.stats() and image.imagram() will console.error internally before returning undefined
            return
        }
        const { width, height } = stats

        const imageUrl = URL.createObjectURL(rgb)

        return {
            imageUrl,
            imagram,
            imagramY,
            width,
            height,
            loading: false,
        }
    } catch (error) {
        console.error("Something went wrong when creating the image preview: ", { error })

        return
    }
}

export const convert16BitImageToVisibleImage = async (buffer: ArrayBuffer) => {
    const processedImage = await (await getSensorTool()).analyzeImageFromArrayBuffer(buffer)
    const rgb = await processedImage.getVisualThermalData()
    return URL.createObjectURL(rgb)
}
